/** @component popover */

import React from 'react';
import PropTypes from 'prop-types';
import { EventOverlay } from '@collab-ui/react';
import omit from 'lodash/omit';

class Popover extends React.Component {

  state = {
    isOpen: false,
    isHovering: false
  };

  componentDidMount() {
    this.props.startOpen &&
    this.delayedShow();
  }

  componentWillUnmount() {
    this.hideTimerId && clearTimeout(this.hideTimerId);
    this.showTimerId && clearTimeout(this.showTimerId);
  }

  delayedHide = e => {
    const { delay, hideDelay, onClose } = this.props;
    const { isHovering } = this.state;
    if ( isHovering ) return;

    if (this.showTimerId) {
      clearTimeout(this.showTimerId);
      this.showTimerId = null;
    }

    const popoverHideTime = hideDelay
      ? hideDelay
      : delay;

    this.hideTimerId = setTimeout(() => {
      this.hideTimerId = null;
      this.setState(
        { isOpen: false, isHovering: false },
        onClose && onClose(e)
      );
    }, popoverHideTime);

    e && e.stopPropagation();
  };

  delayedShow = e => {
    const { delay, showDelay } = this.props;

    if (this.hideTimerId) {
      clearTimeout(this.hideTimerId);
      this.hideTimerId = null;
    }

    const popoverShowTime = showDelay
      ? showDelay
      : delay;

    this.showTimerId = setTimeout(() => {
      this.showTimerId = null;
      this.setState(
        { isOpen: true, isHovering: true }
      );
    }, popoverShowTime);

    e && e.stopPropagation();
  };

  handleClose = e => {
    const { onClose } = this.props;

    this.setState(
      { isOpen: false },
      onClose && onClose(e)
    );
  };

  handleMouseEnter = e => {
    const { children } = this.props;

    children.props.onMouseEnter && children.props.onMouseEnter(e);
    return !this.showTimerId && !this.state.isOpen && this.delayedShow(e);
  };

  delayCheckHover = e => {
    const { hoverDelay } = this.props;

    e.persist();

    this.setState(
      { isHovering: false },
      () => setTimeout(() => this.delayedHide(e), hoverDelay)
    );
  }

  handleMouseLeave = e => {
    const { children } = this.props;
    if (this.hasFocus) {
      return false;
    }

    if (this.showTimerId) {
      clearTimeout(this.showTimerId);
      this.showTimerId = null;
    }

    children.props.onMouseLeave && children.props.onMouseLeave(e);
    return !this.hideTimerId && this.state.isOpen && this.delayCheckHover(e);
  };

  handleBlur = e => {
    const { children } = this.props;
    this.hasFocus = false;

    children.props.onBlur && children.props.onBlur(e);
    this.handleMouseLeave(e);
  };

  handleClick = e => {
    const { children, doesAnchorToggle } = this.props;
    const { isOpen } = this.state;

    this.hasFocus = true;

    children.props.onClick && children.props.onClick(e);

    if(!this.showTimerId) {
      return !isOpen
        ? this.delayedShow(e)
        : doesAnchorToggle && this.handleBlur(e);
    }
  }

  handleFocus = e => {
    const { children } = this.props;
    const { isOpen } = this.state;

    this.hasFocus = true;

    children.props.onFocus && children.props.onFocus(e);

    if(!this.showTimerId) {
      return !isOpen
        ? this.delayedShow(e)
        : this.handleBlur(e);
    }
  };

  render() {
    const { isOpen } = this.state;
    const {
      children,
      className,
      content,
      overflowType,
      popoverTrigger,
      showArrow,
      ...props
    } = this.props;

    const otherProps = omit({...props}, [
      'delay',
      'doesAnchorToggle',
      'hideDelay',
      'hoverDelay',
      'onClose',
      'showDelay',
      'startOpen',
    ]);

    const getTriggers = () => {
      const triggerProps = {};
      triggerProps.ref = ele => (this.anchorRef = ele);
      triggerProps.key = 'child-1';

      switch (popoverTrigger) {
        case 'MouseEnter':
          triggerProps.onMouseEnter = this.handleMouseEnter;
          triggerProps.onMouseLeave = this.handleMouseLeave;

          triggerProps.onFocus = this.handleFocus;
          triggerProps.onBlur = this.handleBlur;
          break;

        case 'Click':
          triggerProps.onClick = this.handleClick;

          triggerProps.onBlur = null;
          triggerProps.onFocus = null;
          triggerProps.onMouseEnter = null;
          triggerProps.onMouseLeave = null;

          break;

        case 'Focus':
          triggerProps.onFocus = this.handleFocus;
          triggerProps.onBlur = this.handleBlur;
          triggerProps.onMouseEnter = null;
          triggerProps.onMouseLeave = null;

          break;

        case 'None':
          triggerProps.onClick = null;
          triggerProps.onFocus = null;
          triggerProps.onBlur = null;
          triggerProps.onMouseEnter = null;
          triggerProps.onMouseLeave = null;

          break;
      }

      return triggerProps;
    };

    const anchorWithTriggers =
      children && React.cloneElement(children, getTriggers());

    return (
      <React.Fragment>
        {anchorWithTriggers}
        <EventOverlay
          anchorNode={this.anchorRef}
          className={className}
          close={this.handleClose}
          isOpen={isOpen}
          ref={ref => this.overlay = ref}
          showArrow={showArrow}
          style={
            { overflow: overflowType }
          }
          {...popoverTrigger === 'MouseEnter' && {
            onMouseEnter: () => {
              this.setState({isHovering: true, isOpen: true});
            },
            onMouseLeave: e => {
              e.persist();
              this.setState(
                {isHovering: false}
                , () => this.delayedHide(e)
              );
            }}
          }
          {...otherProps}
        >
          {content}
        </EventOverlay>
      </React.Fragment>
    );
  }
}

Popover.propTypes = {
  /** @prop Children node that should be the popover trigger(this should be a stateful component) */
  children: PropTypes.element.isRequired,
  /** @prop Optional CSS class names which goes over popover container | '' */
  className: PropTypes.string,
  /** @prop The content that goes into the popover */
  content: PropTypes.oneOfType([PropTypes.element, PropTypes.node]).isRequired,
  /** @prop The delay for popover on hover, click, focus (hide/show) | 0 */
  delay: PropTypes.number,
  /** @prop Boolean for whether the Anchor Toggles the Popover | true */
  doesAnchorToggle: PropTypes.bool,
  /** @prop The hide delay for popover on hover, click, focus | 0 */
  hideDelay: PropTypes.number,
  /** @prop The hover delay for checking whether we are still hovering before closing | 500 */
  hoverDelay: PropTypes.number,
  /** @prop Callback function that will execute on close | null */
  onClose: PropTypes.func,
  /** @prop Optional prop that controls overflow css style of EventOverlay | 'auto' */
  overflowType: PropTypes.string,
  /** @prop Event that will trigger popover appearance | 'MouseEnter' */
  popoverTrigger: PropTypes.oneOf(['MouseEnter', 'Click', 'Focus', 'None']),
   /** @prop Boolean for whether the Arrow should show | true */
  showArrow: PropTypes.bool,
  /** @prop The show delay for popover on hover, click, focus | 0 */
  showDelay: PropTypes.number,
  /** @prop Should Popover start open | false */
  startOpen: PropTypes.bool,
};

Popover.defaultProps = {
  className: '',
  delay: 0,
  doesAnchorToggle: true,
  hideDelay: 0,
  hoverDelay: 500,
  onClose: null,
  overflowType: 'auto',
  popoverTrigger: 'MouseEnter',
  showArrow: true,
  showDelay: 0,
  startOpen: false,
};

Popover.displayName = 'Popover';

export default Popover;



// WEBPACK FOOTER //
// ./src/lib/Popover/index.js